import { http } from '@/app/lib/http/client'

// ---------------------------------------------------------
// 1. Interfaces (Como vienen de la base de datos/API o mocks)
// ---------------------------------------------------------

export interface Supplier {
  id: number
  name: string
  contact: string
  phone: string
  email: string
  products: number
}

export interface PurchaseOrder {
  id: string
  supplier: string
  date: string
  status: 'pending' | 'received' | 'partial'
  items: number
  total: number
}

export interface ProductEntry {
  id: string
  xmlFileName: string
  supplier: string
  date: string
  entryDate: string
  paymentForm: string
  paymentMethod: string
  total: number
  items: number
  status: 'processed' | 'pending'
}

export interface PurchaseTaxRecord {
  id: number
  tax_id: number | null
  tax_kind: string
  tax_code: string
  tax_name: string
  factor_type: string
  tax_rate: string
  taxable_amount: string | null
  tax_amount: string | null
}

export interface PurchaseDocumentTaxRecord {
  id: number
  source: 'implocal'
  tax_kind: 'transfer' | 'withholding'
  tax_name: string
  tax_rate: string | null
  tax_amount: string
}

export interface PurchaseItemRecord {
  id: number
  product_id: number | null
  original_name: string
  original_description: string | null
  original_barcode: string
  sat_product_key_code: string | null
  sat_product_key_name: string | null
  quantity: string | null
  unit_cost_amount: string | null
  subtotal_amount: string | null
  tax_amount: string | null
  total_amount: string | null
  lot_number: string | null
  expiration_date: string | null
  match_status: string
  previous_cost_amount: string | null
  previous_base_price_amount: string | null
  previous_sale_price_amount: string | null
  previous_profit_percentage: string | null
  proposed_cost_amount: string | null
  proposed_sale_price_amount: string | null
  proposed_profit_percentage: string | null
  price_decision: 'applied' | 'pending' | 'kept'
  product?: {
    id: number
    name: string
    barcode: string
    is_active: boolean
  } | null
  taxes?: PurchaseTaxRecord[]
}

export interface PurchaseRecord {
  id: number
  sucursal_id: number
  sucursal: { id: number; name: string }
  source: 'manual' | 'excel' | 'cfdi_xml'
  reference_number: string
  purchase_date: string
  entry_date: string
  payment_condition: 'cash' | 'credit'
  currency: string
  subtotal_amount: string | null
  tax_total_amount: string | null
  total_amount: string | null
  status: string
  price_review_status: string
  document_name: string | null
  cfdi_version?: string | null
  cfdi_uuid?: string | null
  cfdi_series?: string | null
  cfdi_folio?: string | null
  issuer_rfc?: string | null
  issuer_name?: string | null
  receiver_rfc?: string | null
  receiver_name?: string | null
  sat_payment_form_code?: string | null
  sat_payment_method_code?: string | null
  discount_total_amount?: string | null
  withheld_tax_total_amount?: string | null
  items_count: number
  supplier: { id: number; name: string }
  payment_method: { id: number; name: string; code: string }
  created_by?: {
    id: number | null
    source_user_id?: number
    name: string
    last_name?: string | null
    full_name?: string
    email?: string
    is_deleted?: boolean
  }
  taxes?: PurchaseDocumentTaxRecord[]
  items?: PurchaseItemRecord[]
}

export interface AccountsPayablePayment {
  id: number
  amount: string
  payment_date: string
  concept: string | null
}

export interface AccountsPayablePurchase {
  id: number
  reference_number: string
  purchase_date: string
  currency: string
  total_amount: string
  paid_amount: string
  balance_amount: string
  supplier: { id: number; name: string }
  sucursal: { id: number; name: string }
}

export interface AccountsPayableFilters {
  search?: string
  supplier_id?: number
  sucursal_id?: number
  date_from?: string
  date_to?: string
  status?: 'pending' | 'all'
  page?: number
  per_page?: number
}

export interface AccountsPayablePaginator {
  current_page: number
  data: AccountsPayablePurchase[]
  from: number | null
  to: number | null
  per_page: number
  total: number
}

export interface AccountsPayableResult {
  purchases: AccountsPayablePaginator
  totals: {
    total_amount: string
    balance_amount: string
    paid_amount: string
    pending_count: number
  }
  filters: {
    sucursales: Array<{ id: number; name: string }>
    suppliers: Array<{ id: number; name: string }>
  }
}

export interface CreateAccountsPayablePaymentPayload {
  amount: number
  payment_date: string
  concept?: string | null
}

export interface PurchasePaginator {
  current_page: number
  data: PurchaseRecord[]
  from: number | null
  to: number | null
  per_page: number
  total: number
}

export interface PurchaseApiResponse<T> {
  success: boolean
  message: string
  data: T
}

export interface ManualPurchaseItemPayload {
  product_id?: number
  name?: string
  description?: string
  barcode?: string
  sat_product_key_code?: string
  sat_product_key_name?: string
  category_id?: number
  department_id?: number
  product_type_id?: number
  unit_id?: number
  quantity?: number
  cost_without_taxes?: number
  tax_id?: number
  profit_percentage?: number
  lot_number?: string
  expiration_date?: string
  batch_mode?: 'new' | 'existing'
  product_batch_id?: number
}

export interface PurchaseBatchOption {
  id: number
  batch_number: string
  expiration_date: string
  quantity: number
}

export interface ManualPurchasePayload {
  sucursal_id: number
  reference_number: string
  supplier_id: number
  purchase_date: string
  entry_date: string
  payment_condition: 'cash' | 'credit'
  payment_method_id: number
  items: ManualPurchaseItemPayload[]
}

export interface PurchaseExcelResolvedCatalog {
  id: number
  name: string
  requires_lot?: boolean
}

export interface PurchaseExcelPreviewRow {
  row_key: string
  row_number: number
  status: 'new_product' | 'existing_product' | 'blocked'
  imported: {
    sku: string | null
    barcode: string | null
    name: string | null
    description: string | null
    department: string | null
    category: string | null
    unit: string | null
    product_type: string | null
    sat_product_key_code: string | null
    sat_product_key_name: string | null
    quantity: number | null
    cost_without_taxes: string | null
    profit_percentage: string | null
    taxes: Array<{ name: string; type: string; percentage: number }>
  }
  resolved_catalogs: {
    category: PurchaseExcelResolvedCatalog | null
    department: PurchaseExcelResolvedCatalog | null
    unit: PurchaseExcelResolvedCatalog | null
    product_type: PurchaseExcelResolvedCatalog | null
    sat_key: ({ id: number; code: string; name: string }) | null
  }
  suggested_tax: ({ id: number; name: string; type: string; percentage: string | number }) | null
  resolved_product: {
    id: number
    name: string
    description: string | null
    barcode: string
    is_active: boolean
    is_deleted: boolean
    current_stock: number
    category: string | null
    department: string | null
    unit: string | null
    product_type: string | null
    requires_lot: boolean
    sat_product_key_code: string | null
    sat_product_key_name: string | null
    price: {
      cost_without_taxes: string
      final_price: string
      profit_percentage: string
    } | null
  } | null
  errors: string[]
  warnings: string[]
}

export interface PurchaseExcelPreview {
  file: {
    name: string
    size: number
    hash: string
    sheet_name: string | null
    stock_header: string
  }
  summary: {
    total: number
    new_products: number
    existing_products: number
    incomplete: number
    blocked: number
  }
  rows: PurchaseExcelPreviewRow[]
}

export interface ExcelPurchaseItemPayload extends Partial<ManualPurchaseItemPayload> {
  origin: 'manual' | 'excel'
  row_key: string
  row_number?: number
  barcode?: string
}

export interface ExcelPurchasePayload extends Omit<ManualPurchasePayload, 'items'> {
  items: ExcelPurchaseItemPayload[]
}

export interface PurchaseXmlTax {
  kind: 'transfer' | 'withholding'
  code: string
  name: string
  factor_type: string
  rate: string | null
  base: string | null
  amount: string | null
}

export interface PurchaseXmlProduct {
  id: number
  name: string
  description: string | null
  sku: string | null
  barcode: string
  current_stock: number
  category: { id: number; name: string } | null
  department: { id: number; name: string } | null
  unit: { id: number; name: string } | null
  product_type: { id: number; name: string; requires_lot: boolean } | null
  price: { cost_without_taxes: string; sale_price: string; final_price: string; profit_percentage: string } | null
}

export interface PurchaseXmlPreviewRow {
  row_key: string
  row_number: number
  identifier: string | null
  sat_product_key_code: string | null
  name: string
  description: string
  quantity: string
  gross_unit_cost: string
  net_unit_cost: string | null
  subtotal: string
  discount: string
  tax_total: string
  total: string
  sat_unit_code: string | null
  sat_unit_name: string | null
  tax_object_code: string | null
  taxes: PurchaseXmlTax[]
  status: 'existing_product' | 'unmatched' | 'blocked'
  errors: string[]
  warnings: string[]
  resolved_product: PurchaseXmlProduct | null
  suggested_tax: { id: number; name: string; type: string; percentage: string | number } | null
  resolved_catalogs: {
    category: { id: number; name: string } | null
    department: { id: number; name: string } | null
    product_type: { id: number; name: string; requires_lot?: boolean } | null
    unit: { id: number; name: string } | null
    sat_key: { id: number; code: string; name: string } | null
  }
  cost_comparison: {
    previous_cost: string | null
    previous_base_price: string | null
    cfdi_net_cost: string | null
    change_percentage: number | null
    direction: 'increase' | 'decrease' | 'unchanged' | 'no_previous_cost'
  }
}

export interface PurchaseXmlPreview {
  file: { name: string; size: number; hash: string }
  cfdi: {
    version: '3.3' | '4.0'
    uuid: string
    series: string | null
    folio: string | null
    issued_at: string
    issuer: { rfc: string; name: string }
    receiver: { rfc: string; name: string }
    currency: string
    exchange_rate: string
    sat_payment_form_code: string | null
    sat_payment_method_code: string | null
    subtotal: string
    discount_total: string
    federal_tax_total?: string
    federal_withheld_tax_total?: string
    local_tax_total?: string
    local_withheld_tax_total?: string
    tax_total: string
    withheld_tax_total: string
    total: string
    complements?: Array<{ code: string; name: string; namespace: string; version: string | null }>
    document_taxes?: Array<{
      source: 'implocal'; kind: 'transfer' | 'withholding'; name: string; rate: string | null; amount: string
    }>
  }
  suggestions: {
    supplier: { id: number; name: string; rfc: string | null; is_active: boolean; blocked: boolean } | null
    payment_condition: 'cash' | 'credit'
    payment_method: { id: number; name: string; code: string } | null
  }
  warnings: Array<{ code: string; message: string; requires_acknowledgement: boolean }>
  summary: {
    total: number; existing_products: number; unmatched: number; non_inventory: number; blocked: number
    increases: number; decreases: number; unchanged: number; without_previous_cost: number
  }
  rows: PurchaseXmlPreviewRow[]
}

export interface PurchaseXmlDecision {
  row_key: string
  resolution: 'existing_product' | 'new_product' | 'non_inventory'
  product_id?: number
  name?: string
  description?: string
  barcode?: string
  sat_product_key_code?: string
  sat_product_key_name?: string
  category_id?: number
  department_id?: number
  product_type_id?: number
  unit_id?: number
  tax_id?: number
  profit_percentage?: number
  lot_number?: string
  expiration_date?: string
  batch_mode?: 'new' | 'existing'
  product_batch_id?: number
}

export interface PurchaseXmlPayload {
  sucursal_id: number
  supplier_id: number
  entry_date: string
  payment_condition: 'cash' | 'credit'
  payment_method_id: number
  acknowledged_warnings: string[]
  items: PurchaseXmlDecision[]
}

export interface ProductFromXML {
  id: number
  name: string
  quantity: number
  newPrice: number
  previousPrice: number
  priceChange: number
  satKey: string
  lotNumber: string
  salePrice: number
  margin: number
  supplier: string
}

export interface SuggestedProduct {
  id: number
  name: string
  sku: string
  barcode: string | null
  currentStock: number
  avgSales: number
  daysRemaining: number | null
  suggested: number
  supplier: {
    id: number
    name: string
    lastUnitCost: number
  } | null
  period: {
    dateFrom: string
    dateTo: string
    days: number
  }
  branches: Array<{
    id: number
    name: string
    currentStock: number
    stockMin: number
    stockMax: number
    avgSales: number
    daysRemaining: number | null
    suggested: number
  }>
}

export interface SuggestedOrderPaginator {
  current_page: number
  data: SuggestedProduct[]
  from: number | null
  to: number | null
  per_page: number
  total: number
}

interface SuggestedOrderApiRecord {
  product: { id: number; name: string; sku: string; barcode: string | null }
  period: { date_from: string; date_to: string; days: number }
  current_stock: number
  average_daily_sales: number
  days_remaining: number | null
  suggested_quantity: number
  supplier: { id: number; name: string; last_unit_cost: string | number } | null
  branches: Array<{
    sucursal: { id: number; name: string }
    current_stock: number
    stock_min: number
    stock_max: number
    average_daily_sales: number
    days_remaining: number | null
    suggested_quantity: number
  }>
}

function mapSuggestedProduct(record: SuggestedOrderApiRecord): SuggestedProduct {
  return {
    id: record.product.id,
    name: record.product.name,
    sku: record.product.sku,
    barcode: record.product.barcode,
    currentStock: record.current_stock,
    avgSales: record.average_daily_sales,
    daysRemaining: record.days_remaining,
    suggested: record.suggested_quantity,
    supplier: record.supplier ? {
      id: record.supplier.id,
      name: record.supplier.name,
      lastUnitCost: Number(record.supplier.last_unit_cost),
    } : null,
    period: {
      dateFrom: record.period.date_from,
      dateTo: record.period.date_to,
      days: record.period.days,
    },
    branches: record.branches.map((branch) => ({
      id: branch.sucursal.id,
      name: branch.sucursal.name,
      currentStock: branch.current_stock,
      stockMin: branch.stock_min,
      stockMax: branch.stock_max,
      avgSales: branch.average_daily_sales,
      daysRemaining: branch.days_remaining,
      suggested: branch.suggested_quantity,
    })),
  }
}

export interface NewProductData {
  id: number
  name: string
  satKey: string
  categoryId: string
  departmentId: string
  productTypeId: string
  requiresLot?: boolean
  expiryDate: string
  lotNumber?: string
  unitId: string
  description: string
  isRegistered: boolean
  barcode?: string
  quantity?: number
  cost?: number
  margin?: number
  salePrice?: number
  taxId?: string
}

export interface PriceAdjustment {
  id: string
  supplier: string
  date: string
  purchaseId: string
  purchaseNumericId: number
  purchaseReference: string
  status: 'pending' | 'updated'
  items: number
  products: PriceAdjustmentProduct[]
  isEdited?: boolean
  editDate?: string
}

export interface PriceAdjustmentProduct {
  id: number
  name: string
  quantity: number
  previousCost: number | null
  newCost: number
  costChange: number | null
  previousSalePrice: number | null
  previousMargin: number | null
  newSalePrice: number | null
  newMargin: number | null
  lotNumber: string
  lots: string[]
  previousBasePrice: number | null
  taxPercentage: number
  decision: 'pending' | 'applied' | 'kept'
  calculationSource?: 'final_price' | 'profit_percentage'
  canAdjust: boolean
  blockingReason: string | null
  canKeepPrevious: boolean
  keepBlockingReason: string | null
}

export interface PriceAdjustmentPaginator {
  current_page: number
  data: PriceAdjustment[]
  from: number | null
  to: number | null
  per_page: number
  total: number
}

export interface SavePriceAdjustmentPayload {
  items: Array<{
    product_id: number
    decision: 'apply' | 'keep'
    calculation_source?: 'final_price' | 'profit_percentage'
    final_price?: number
    profit_percentage?: number
  }>
}

interface PriceAdjustmentApiProduct {
  product_id: number
  name: string
  quantity: string
  lots: string[]
  lot_display: string
  previous_cost: string | null
  previous_base_price: string | null
  previous_final_price: string | null
  previous_profit_percentage: string | null
  new_cost: string
  cost_change_percentage: number | null
  tax: { percentage: string | null } | null
  proposal: { final_price: string; profit_percentage: string } | null
  decision: 'pending' | 'applied' | 'kept'
  adjusted_final_price: string | null
  adjusted_profit_percentage: string | null
  can_adjust: boolean
  blocking_reason: string | null
  can_keep_previous: boolean
  keep_blocking_reason: string | null
}

interface PriceAdjustmentApiRecord {
  id: string
  purchase_id: number
  purchase_code: string
  purchase_reference: string
  supplier: { id: number; name: string } | null
  date: string
  status: 'pending' | 'updated'
  items: number
  is_edited: boolean
  modified_at: string | null
  products?: PriceAdjustmentApiProduct[]
}

function nullableNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function mapPriceAdjustment(record: PriceAdjustmentApiRecord): PriceAdjustment {
  return {
    id: record.id,
    supplier: record.supplier?.name ?? 'Sin proveedor',
    date: record.date,
    purchaseId: record.purchase_code,
    purchaseNumericId: record.purchase_id,
    purchaseReference: record.purchase_reference,
    status: record.status,
    items: record.items,
    isEdited: record.is_edited,
    editDate: record.modified_at ? record.modified_at.slice(0, 10) : undefined,
    products: (record.products ?? []).map((product) => ({
      id: product.product_id,
      name: product.name,
      quantity: Number(product.quantity),
      previousCost: nullableNumber(product.previous_cost),
      newCost: Number(product.new_cost),
      costChange: nullableNumber(product.cost_change_percentage),
      previousSalePrice: nullableNumber(product.previous_final_price),
      previousMargin: nullableNumber(product.previous_profit_percentage),
      newSalePrice: nullableNumber((product.decision === 'kept' ? null : product.adjusted_final_price) ?? product.proposal?.final_price ?? product.previous_final_price),
      newMargin: nullableNumber((product.decision === 'kept' ? null : product.adjusted_profit_percentage) ?? product.proposal?.profit_percentage ?? product.previous_profit_percentage),
      lotNumber: product.lot_display,
      lots: product.lots,
      previousBasePrice: product.previous_base_price === null ? null : Number(product.previous_base_price),
      taxPercentage: Number(product.tax?.percentage ?? 0),
      decision: product.decision,
      calculationSource: 'profit_percentage',
      canAdjust: product.can_adjust,
      blockingReason: product.blocking_reason,
      canKeepPrevious: product.can_keep_previous,
      keepBlockingReason: product.keep_blocking_reason,
    })),
  }
}

// ---------------------------------------------------------
// 2. Interfaces para Crear/Editar (Lo que mandas en POST/PUT)
// ---------------------------------------------------------

export interface CreateSupplierPayload {
  name: string
  contact: string
  phone: string
  email: string
  website?: string
  catalogType?: 'url' | 'file'
}

export interface CreateProductEntryPayload {
  paymentForm: string
  paymentMethod: string
  entryDate: string
  products: ProductFromXML[]
  newProducts?: NewProductData[]
}

// ---------------------------------------------------------
// 3. Mock Data
// ---------------------------------------------------------

export const mockSuppliers: Supplier[] = [
  {
    id: 1,
    name: 'Laboratorios Pisa',
    contact: 'Juan Pérez',
    phone: '555-1234',
    email: 'ventas@pisa.com.mx',
    products: 45,
  },
  {
    id: 2,
    name: 'Farmacéutica GNP',
    contact: 'María González',
    phone: '555-5678',
    email: 'pedidos@gnp.com.mx',
    products: 38,
  },
  {
    id: 3,
    name: 'Distribuidora Farmacéutica',
    contact: 'Carlos Méndez',
    phone: '555-9012',
    email: 'contacto@distfarma.com',
    products: 52,
  },
]

export const mockProductEntries: ProductEntry[] = [
  /*
  {
    id: 'ING-001',
    xmlFileName: 'factura_pisa_202604_001.xml',
    supplier: 'Laboratorios Pisa',
    date: '2026-04-25',
    entryDate: '2026-04-26',
    paymentForm: 'Crédito',
    paymentMethod: 'Transferencia',
    total: 12500,
    items: 15,
    status: 'processed',
  },
  {
    id: 'ING-002',
    xmlFileName: 'factura_gnp_202604_045.xml',
    supplier: 'Farmacéutica GNP',
    date: '2026-04-24',
    entryDate: '2026-04-24',
    paymentForm: 'Contado',
    paymentMethod: 'Efectivo',
    total: 18900,
    items: 22,
    status: 'processed',
  },
  */
]

export const mockPriceAdjustments = [
  {
    id: 'ADJ-001',
    supplier: 'Laboratorios Pisa',
    date: '2026-05-10',
    purchaseId: 'ING-003',
    status: 'pending',
    items: 3,
    products: [
      {
        id: 1,
        name: 'Paracetamol 500mg',
        quantity: 100,
        previousCost: 45.0,
        newCost: 48.0,
        costChange: 6.67,
        previousSalePrice: 70.0,
        previousMargin: 35.56,
        newSalePrice: 75.0,
        newMargin: 35.42,
        lotNumber: 'LOT-2026-04-001',
      },
      {
        id: 2,
        name: 'Loratadina 10mg',
        quantity: 60,
        previousCost: 120.0,
        newCost: 130.0,
        costChange: 8.33,
        previousSalePrice: 180.0,
        previousMargin: 33.33,
        newSalePrice: 195.0,
        newMargin: 33.33,
        lotNumber: 'LOT-2026-04-004',
      },
      {
        id: 3,
        name: 'Omeprazol 20mg',
        quantity: 75,
        previousCost: 95.0,
        newCost: 92.0,
        costChange: -3.16,
        previousSalePrice: 145.0,
        previousMargin: 34.48,
        newSalePrice: 140.0,
        newMargin: 34.29,
        lotNumber: 'LOT-2026-04-005',
      },
    ],
  },
  {
    id: 'ADJ-002',
    supplier: 'Farmacéutica GNP',
    date: '2026-05-08',
    purchaseId: 'ING-002',
    status: 'updated',
    items: 2,
    isEdited: true,
    editDate: '2026-05-09',
    products: [
      {
        id: 4,
        name: 'Ibuprofeno 400mg',
        quantity: 80,
        previousCost: 65.0,
        newCost: 62.0,
        costChange: -4.62,
        previousSalePrice: 100.0,
        previousMargin: 35.0,
        newSalePrice: 95.0,
        newMargin: 34.74,
        lotNumber: 'LOT-2026-04-002',
      },
      {
        id: 5,
        name: 'Amoxicilina 500mg',
        quantity: 50,
        previousCost: 180.0,
        newCost: 180.0,
        costChange: 0,
        previousSalePrice: 270.0,
        previousMargin: 33.33,
        newSalePrice: 270.0,
        newMargin: 33.33,
        lotNumber: 'LOT-2026-04-003',
      },
    ],
  },
  {
    id: 'ADJ-003',
    supplier: 'Distribuidora Farmacéutica',
    date: '2026-05-05',
    purchaseId: 'ING-004',
    status: 'updated',
    items: 1,
    products: [
      {
        id: 6,
        name: 'Diclofenaco 100mg',
        quantity: 45,
        previousCost: 85.0,
        newCost: 88.0,
        costChange: 3.53,
        previousSalePrice: 130.0,
        previousMargin: 34.62,
        newSalePrice: 135.0,
        newMargin: 34.81,
        lotNumber: 'LOT-2026-04-006',
      },
    ],
  },
]

export const mockExtractedProducts: ProductFromXML[] = [
  {
    id: 1,
    name: 'Paracetamol 500mg',
    quantity: 100,
    newPrice: 48.0,
    previousPrice: 45.0,
    priceChange: 6.67,
    satKey: '50161501',
    lotNumber: 'LOT-2026-04-001',
    salePrice: 75.0,
    margin: 35,
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 2,
    name: 'Ibuprofeno 400mg',
    quantity: 80,
    newPrice: 62.0,
    previousPrice: 65.0,
    priceChange: -4.62,
    satKey: '50161502',
    lotNumber: 'LOT-2026-04-002',
    salePrice: 95.0,
    margin: 30,
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 3,
    name: 'Amoxicilina 500mg',
    quantity: 50,
    newPrice: 180.0,
    previousPrice: 180.0,
    priceChange: 0,
    satKey: '51101506',
    lotNumber: 'LOT-2026-04-003',
    salePrice: 270.0,
    margin: 40,
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 4,
    name: 'Loratadina 10mg',
    quantity: 60,
    newPrice: 130.0,
    previousPrice: 120.0,
    priceChange: 8.33,
    satKey: '51101801',
    lotNumber: 'LOT-2026-04-004',
    salePrice: 195.0,
    margin: 38,
    supplier: 'Laboratorios Pisa',
  },
  {
    id: 5,
    name: 'Omeprazol 20mg',
    quantity: 75,
    newPrice: 92.0,
    previousPrice: 95.0,
    priceChange: -3.16,
    satKey: '51171701',
    lotNumber: 'LOT-2026-04-005',
    salePrice: 140.0,
    margin: 32,
    supplier: 'Laboratorios Pisa',
  },
]

export const mockExcelProducts: NewProductData[] = [
  {
    id: 1,
    name: 'Aspirina 500mg',
    satKey: '51121701',
    categoryId: '',
    departmentId: '',
    productTypeId: '',
    expiryDate: '',
    unitId: '',
    description: '',
    isRegistered: false,
  },
  {
    id: 2,
    name: 'Diclofenaco Gel',
    satKey: '53131626',
    categoryId: '',
    departmentId: '',
    productTypeId: '',
    expiryDate: '',
    unitId: '',
    description: '',
    isRegistered: false,
  },
]

// ---------------------------------------------------------
// 4. El Objeto de Servicio con todas las peticiones
// ---------------------------------------------------------

export const purchasesService = {
  // === MOCK METHODS (CURRENTLY USED) ===

  async getSuppliers() {
    return Promise.resolve(mockSuppliers)
  },

  async getProductEntries(params?: {
    search?: string
    supplier_id?: number
    sucursal_id?: number
    status?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
  }): Promise<PurchaseApiResponse<PurchasePaginator>> {
    const response = await http.get('/purchases', { params })
    return response.data
  },

  async getProductEntry(id: number): Promise<PurchaseApiResponse<PurchaseRecord>> {
    const response = await http.get(`/purchases/${id}`)
    return response.data
  },

  async getAccountsPayable(params: AccountsPayableFilters = {}): Promise<PurchaseApiResponse<AccountsPayableResult>> {
    const response = await http.get('/purchases/accounts-payable', { params })
    return response.data
  },

  async createAccountsPayablePayment(
    purchaseId: number,
    payload: CreateAccountsPayablePaymentPayload,
  ): Promise<PurchaseApiResponse<AccountsPayablePurchase>> {
    const response = await http.post(`/purchases/${purchaseId}/payments`, payload)
    return response.data
  },

  async getAccountsPayablePayments(purchaseId: number): Promise<PurchaseApiResponse<AccountsPayablePayment[]>> {
    const response = await http.get(`/purchases/${purchaseId}/payments`)
    return response.data
  },

  async createManualPurchase(payload: ManualPurchasePayload): Promise<PurchaseApiResponse<PurchaseRecord>> {
    const response = await http.post('/purchases/manual', payload)
    return response.data
  },

  async downloadExcelTemplate(): Promise<Blob> {
    const response = await http.get('/purchases/excel/template', { responseType: 'blob' })
    return response.data
  },

  async previewExcel(file: File, sucursalId: number): Promise<PurchaseApiResponse<PurchaseExcelPreview>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('sucursal_id', String(sucursalId))
    const response = await http.post('/purchases/excel/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  async createExcelPurchase(payload: ExcelPurchasePayload, file: File): Promise<PurchaseApiResponse<PurchaseRecord>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('sucursal_id', String(payload.sucursal_id))
    formData.append('reference_number', payload.reference_number)
    formData.append('supplier_id', String(payload.supplier_id))
    formData.append('purchase_date', payload.purchase_date)
    formData.append('entry_date', payload.entry_date)
    formData.append('payment_condition', payload.payment_condition)
    formData.append('payment_method_id', String(payload.payment_method_id))
    formData.append('items', JSON.stringify(payload.items))
    const response = await http.post('/purchases/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  async previewXml(file: File, sucursalId: number): Promise<PurchaseApiResponse<PurchaseXmlPreview>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('sucursal_id', String(sucursalId))
    const response = await http.post('/purchases/xml/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  async createXmlPurchase(payload: PurchaseXmlPayload, file: File): Promise<PurchaseApiResponse<PurchaseRecord>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('sucursal_id', String(payload.sucursal_id))
    formData.append('supplier_id', String(payload.supplier_id))
    formData.append('entry_date', payload.entry_date)
    formData.append('payment_condition', payload.payment_condition)
    formData.append('payment_method_id', String(payload.payment_method_id))
    formData.append('acknowledged_warnings', JSON.stringify(payload.acknowledged_warnings))
    formData.append('items', JSON.stringify(payload.items))
    const response = await http.post('/purchases/xml', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  async getPurchaseProductOptions(params?: { sucursal_id?: number; search?: string; page?: number; per_page?: number }) {
    const response = await http.get('/purchases/product-options', { params })
    return response.data
  },

  async getPurchaseBatchOptions(productId: number, sucursalId: number): Promise<PurchaseApiResponse<PurchaseBatchOption[]>> {
    const response = await http.get(`/purchases/products/${productId}/batches`, { params: { sucursal_id: sucursalId } })
    return response.data
  },

  async downloadPurchaseDocument(id: number): Promise<Blob> {
    const response = await http.get(`/purchases/${id}/document`, { responseType: 'blob' })
    return response.data
  },

  async getSuggestedProducts(params?: {
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
  }): Promise<PurchaseApiResponse<SuggestedOrderPaginator>> {
    const response = await http.get('/purchases/suggested-orders', { params })
    return {
      ...response.data,
      data: {
        ...response.data.data,
        data: response.data.data.data.map(mapSuggestedProduct),
      },
    }
  },

  async getPriceAdjustments(params?: {
    supplier_id?: number
    status?: 'pending' | 'updated'
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
  }): Promise<PurchaseApiResponse<PriceAdjustmentPaginator>> {
    const response = await http.get('/purchases/price-adjustments', { params })
    return {
      ...response.data,
      data: {
        ...response.data.data,
        data: response.data.data.data.map(mapPriceAdjustment),
      },
    }
  },

  async getPriceAdjustment(purchaseId: number): Promise<PurchaseApiResponse<PriceAdjustment>> {
    const response = await http.get(`/purchases/price-adjustments/${purchaseId}`)
    return { ...response.data, data: mapPriceAdjustment(response.data.data) }
  },

  async applyPriceAdjustment(purchaseId: number, payload: SavePriceAdjustmentPayload): Promise<PurchaseApiResponse<PriceAdjustment>> {
    const response = await http.post(`/purchases/price-adjustments/${purchaseId}/apply`, payload)
    return { ...response.data, data: mapPriceAdjustment(response.data.data) }
  },

  async updatePriceAdjustment(purchaseId: number, payload: SavePriceAdjustmentPayload): Promise<PurchaseApiResponse<PriceAdjustment>> {
    const response = await http.put(`/purchases/price-adjustments/${purchaseId}`, payload)
    return { ...response.data, data: mapPriceAdjustment(response.data.data) }
  },

  async processXmlMock() {
    return Promise.resolve(mockExtractedProducts)
  },

  async processExcelMock() {
    return Promise.resolve(mockExcelProducts)
  },


  // === BACKEND CONNECTION PREPARATION (TODO: USE THESE IN THE FUTURE) ===
  
  /*
  // Obtener proveedores
  async apiGetSuppliers(params?: { per_page?: number; search?: string; page?: number }) {
    const response = await http.get('/api/suppliers', { params }) // TODO: CAMBIAR RUTA
    return response.data
  },

  // Obtener un proveedor por ID
  async apiGetSupplier(id: number) {
    const response = await http.get(`/api/suppliers/${id}`) // TODO: CAMBIAR RUTA
    return response.data
  },

  // Crear proveedor
  async apiCreateSupplier(payload: CreateSupplierPayload) {
    const response = await http.post('/api/suppliers', payload) // TODO: CAMBIAR RUTA
    return response.data
  },

  // Obtener compras/ingresos
  async apiGetPurchases(params?: { per_page?: number; search?: string; page?: number }) {
    const response = await http.get('/api/purchases', { params }) // TODO: CAMBIAR RUTA
    return response.data
  },

  // Crear ingreso de mercancía (compra)
  async apiCreatePurchase(payload: CreateProductEntryPayload) {
    const response = await http.post('/api/purchases', payload) // TODO: CAMBIAR RUTA
    return response.data
  },

  // Obtener sugerencias de pedido
  async apiGetSuggestedOrders() {
    const response = await http.get('/api/purchases/suggested') // TODO: CAMBIAR RUTA
    return response.data
  },

  // Obtener ajustes de precios
  async apiGetPriceAdjustments() {
    const response = await http.get('/api/purchases/adjustments') // TODO: CAMBIAR RUTA
    return response.data
  }
  */
}
